import { useState, useCallback } from 'react';

// Define input types and their validation patterns
export type InputValidationType =
  | 'zipCode'
  | 'number'
  | 'textOnly'
  | 'textWithSpecialChars'
  | 'currency'
  | 'phone'
  | 'phone10Digit'
  | 'email';

// Validation patterns
const VALIDATION_PATTERNS = {
  zipCode: /^\d{0,5}$/,
  number: /^\d*$/,
  textOnly: /^[a-zA-Z\s]*$/,
  textWithSpecialChars: /^[a-zA-Z0-9\s.,!?@#$%&*()_+=[\]{}|;':"\\/<>-]*$/,
  currency: /^\d*\.?\d{0,2}$/,
  phone: /^[\d\s-()]*$/,
  phone10Digit: /^\d{0,10}$/,
  email: /^[a-zA-Z0-9@._-]*$/,
} as const;

// Error messages for each validation type
const ERROR_MESSAGES = {
  zipCode: 'ZIP code must be exactly 5 digits',
  number: 'Only numbers are allowed',
  textOnly: 'Only letters and spaces are allowed',
  textWithSpecialChars: 'Invalid characters detected',
  currency: 'Invalid currency format (e.g., 123.45)',
  phone: 'Invalid phone number format',
  phone10Digit: 'Phone number must be exactly 10 digits',
  email: 'Invalid email format',
} as const;

// Length limits for different input types
const MAX_LENGTHS = {
  zipCode: 5,
  number: undefined,
  textOnly: undefined,
  textWithSpecialChars: undefined,
  currency: undefined,
  phone: 14, // (XXX) XXX-XXXX
  phone10Digit: 10,
  email: undefined,
} as const;

export interface UseInputValidationReturn {
  value: string;
  isValid: boolean;
  error: string;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  reset: () => void;
  setValue: (value: string) => void;
  maxLength?: number;
}

/**
 * Custom hook for input validation with real-time restriction and validation
 *
 * @param type - The type of validation to apply
 * @param initialValue - Initial value for the input
 * @param customValidator - Optional custom validation function
 * @returns Object with validation state and handlers
 */
export const useInputValidation = (
  type: InputValidationType,
  initialValue = '',
  customValidator?: (value: string) => string,
): UseInputValidationReturn => {
  const [value, setValue] = useState<string>(initialValue);
  const [error, setError] = useState<string>('');

  const pattern = VALIDATION_PATTERNS[type];
  const maxLength = MAX_LENGTHS[type];

  // Validate the input value
  const validateInput = useCallback(
    (inputValue: string): boolean => {
      // Check pattern validation
      if (!pattern.test(inputValue)) {
        setError(ERROR_MESSAGES[type]);
        return false;
      }

      // For ZIP code, check if it's exactly 5 digits when not empty
      if (
        type === 'zipCode' &&
        inputValue.length > 0 &&
        inputValue.length !== 5
      ) {
        setError(ERROR_MESSAGES[type]);
        return false;
      }

      // For 10-digit phone, check if it's exactly 10 digits when not empty
      if (
        type === 'phone10Digit' &&
        inputValue.length > 0 &&
        inputValue.length !== 10
      ) {
        setError(ERROR_MESSAGES[type]);
        return false;
      }

      // For currency, ensure proper decimal places
      if (type === 'currency' && inputValue.includes('.')) {
        const decimalPart = inputValue.split('.')[1];
        if (decimalPart && decimalPart.length > 2) {
          setError(ERROR_MESSAGES[type]);
          return false;
        }
      }

      // Custom validation if provided
      if (customValidator) {
        const customError = customValidator(inputValue);
        if (customError) {
          setError(customError);
          return false;
        }
      }

      setError('');
      return true;
    },
    [type, pattern, customValidator],
  );

  // Handle input change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Apply length restriction if specified
      if (maxLength && inputValue.length > maxLength) {
        return;
      }

      // For ZIP code, only allow digits and restrict to 5 characters
      if (type === 'zipCode') {
        if (!/^\d*$/.test(inputValue) || inputValue.length > 5) {
          return;
        }
      }

      // For 10-digit phone, only allow digits and restrict to 10 characters
      if (type === 'phone10Digit') {
        if (!/^\d*$/.test(inputValue) || inputValue.length > 10) {
          return;
        }
      }

      // For currency, format the input
      if (type === 'currency') {
        // Remove any non-digit and non-decimal characters
        const cleanedValue = inputValue.replace(/[^\d.]/g, '');

        // Ensure only one decimal point
        const parts = cleanedValue.split('.');
        if (parts.length > 2) {
          return;
        }

        // Limit to 2 decimal places
        if (parts[1] && parts[1].length > 2) {
          return;
        }

        setValue(cleanedValue);
        validateInput(cleanedValue);
        return;
      }

      // For other types, check pattern before setting value
      if (pattern.test(inputValue)) {
        setValue(inputValue);
        validateInput(inputValue);
      }
    },
    [type, maxLength, pattern, validateInput],
  );

  // Handle key press for additional restrictions
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // For ZIP code, only allow digits
      if (type === 'zipCode') {
        if (
          !/\d/.test(e.key) &&
          !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(
            e.key,
          )
        ) {
          e.preventDefault();
        }
      }

      // For number type, only allow digits
      if (type === 'number') {
        if (
          !/\d/.test(e.key) &&
          !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(
            e.key,
          )
        ) {
          e.preventDefault();
        }
      }

      // For 10-digit phone, only allow digits
      if (type === 'phone10Digit') {
        if (
          !/\d/.test(e.key) &&
          !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(
            e.key,
          )
        ) {
          e.preventDefault();
        }
      }

      // For currency, allow digits, decimal point, and control keys
      if (type === 'currency') {
        if (
          !/[\d.]/.test(e.key) &&
          !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(
            e.key,
          )
        ) {
          e.preventDefault();
        }
      }
    },
    [type],
  );

  // Reset the input
  const reset = useCallback(() => {
    setValue('');
    setError('');
  }, []);

  // Set value programmatically
  const setValueProgrammatically = useCallback(
    (newValue: string) => {
      setValue(newValue);
      validateInput(newValue);
    },
    [validateInput],
  );

  const isValid =
    !error &&
    (type === 'zipCode'
      ? value.length === 5 || value.length === 0
      : type === 'phone10Digit'
        ? value.length === 10 || value.length === 0
        : true);

  return {
    value,
    isValid,
    error,
    handleChange,
    handleKeyPress,
    reset,
    setValue: setValueProgrammatically,
    maxLength,
  };
};
